import { Suspense, lazy, useEffect, useState } from "react";
import { hasPendingTap } from "@/components/tap-feedback-store";

// The TapFeedbackModal pulls in the Radix Sheet/Dialog. It only ever needs to
// render ~3 minutes AFTER a buyer taps WhatsApp, so we keep it out of the
// critical-path entry chunk and lazy-load it the moment a tap is registered.
const TapFeedbackModal = lazy(() => import("@/components/TapFeedbackModal"));

/**
 * Tiny, always-mounted listener at the app root. Dependency-free except for the
 * (Sheet-free) tap store, so anonymous visitors who never tap WhatsApp never
 * download the modal chunk.
 *
 * Contract preserved: it subscribes to the same `voom:whatsapp-tap` event and
 * reads the same module-level store as before. On the first pending tap it
 * mounts the lazy modal, which then runs its own existing 3-minute timer logic
 * (on-mount `scheduleDeferredCheck`) to surface itself — identical UX to when
 * the modal was eagerly mounted at the app root.
 */
export default function TapFeedbackListener() {
  // Once armed, the modal stays mounted for the rest of the session (it's just
  // event listeners + a closed Sheet) and owns the show/delay logic itself.
  const [armed, setArmed] = useState(() => hasPendingTap());

  useEffect(() => {
    if (armed) return;
    // Re-check on mount: the tap may have fired between the initial useState
    // call and this effect (e.g. ProductCard's dynamic-import race).
    if (hasPendingTap()) {
      setArmed(true);
      return;
    }
    const onTap = () => setArmed(true);
    window.addEventListener("voom:whatsapp-tap", onTap);
    return () => window.removeEventListener("voom:whatsapp-tap", onTap);
  }, [armed]);

  if (!armed) return null;

  return (
    <Suspense fallback={null}>
      <TapFeedbackModal />
    </Suspense>
  );
}
